import isEmpty from 'lodash/isEmpty'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { usePrevilegies } from 'hooks/usePrevilegies'
import { projectInfoSelector } from 'store/projects'
import { currentUserSelector } from 'store/users'
import { CardLogo } from 'ui/components/CardLogo'
import Icon from 'ui/components/Icon'
import { ProjectEditModal } from 'ui/modules/Projects/components/ProjectEdit'
import { TEMPLATES_ROUTE } from './constants'
import { useMenu } from './hooks'
import {
  Wrapper,
  List,
  Elem,
  ControlIcon,
  IconImg,
  ControlTitle,
  ProjectMenu,
  ProjectMenuBox,
  ProjectMenuList,
  ProjectMenuTitleStyled,
  ProjectLogo,
  ProjectName,
  ElemButton,
  ChevronIcon,
  ConfidentialityNotice,
} from './styled'

export const Menu = ({ setMenuOpened, hide }) => {
  const router = useRouter()
  const { generalMenu, projectMenu, editProjectOpened, setEditProjectOpened } =
    useMenu()
  const { isManager } = usePrevilegies()
  const currentUser = useSelector(currentUserSelector)
  const projectInfo = useSelector(projectInfoSelector)

  const [openGroups, setOpenGroups] = useState({})

  const checkIsActive = (href) => {
    const match = router.pathname.startsWith(href)

    if (href === TEMPLATES_ROUTE) {
      return router.pathname.startsWith(TEMPLATES_ROUTE)
    }

    if (!match || !href.startsWith('/users')) {
      return router.pathname === href
    }

    const userPage = router.pathname.startsWith('/users/')
    const allUsersPage = router.pathname === '/users'
    const matchUserId = Number(router.query.id) === Number(currentUser?.id)
    if (href === '/users') {
      return allUsersPage || (userPage && !matchUserId)
    }
    return userPage && matchUserId
  }

  const navClick = (event, url, query) => {
    event.preventDefault()
    query?.project
      ? router.push({
          pathname: url,
          query: {
            project: query.project,
            group: query.group,
            hideProjectSelect: 1,
            hideUserSelect: isManager ? 0 : 1,
            getReport: 1,
            user: isManager ? '' : currentUser?.id,
          },
          url,
        })
      : router.push(url)
    setMenuOpened(false)
  }

  const visibleProjectMenu = router.pathname?.startsWith('/projects/')

  const isItemActive = (item) => !!item?.asHref && checkIsActive(item.asHref)
  const isGroupActive = (group) =>
    group?.children?.some((child) => isItemActive(child))
  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  useEffect(() => {
    const allMenus = [...projectMenu, ...generalMenu]

    const groups = allMenus.filter((item) => item?.children?.length)
    if (!groups.length) return

    const next = {}

    groups.forEach((group) => {
      if (!group.key) return
      if (isGroupActive(group)) {
        next[group.key] = true
      }
    })

    if (!isEmpty(next)) {
      setOpenGroups((prev) => ({ ...prev, ...next }))
    }
  }, [projectMenu, generalMenu, router.asPath])

  const projectMenuTitle = (
    <ProjectMenuTitleStyled>
      <ProjectLogo>
        <CardLogo
          name={projectInfo?.name}
          logo={projectInfo?.logo?.link}
          isApi={projectInfo?.logo?.is_api}
        />
      </ProjectLogo>
      <ProjectName>{projectInfo?.name}</ProjectName>
    </ProjectMenuTitleStyled>
  )

  const renderMenu = (items, level = 0) =>
    items.map((item, index) => {
      const key = item.key || item.title || `${level}-${index}`

      if (item?.children?.length) {
        const opened = !!openGroups[key]
        const groupActive = isGroupActive(item)

        return (
          <div key={key}>
            <ElemButton
              type="button"
              title={item.title}
              isActive={groupActive}
              onClick={() => toggleGroup(key)}
              style={{ '--menu-item-icon': item.iconColor }}
            >
              <div>
                <ControlIcon>
                  {item?.iconHref ? (
                    <IconImg src={item.iconHref} />
                  ) : (
                    <Icon name={item.icon} />
                  )}
                </ControlIcon>
                <ControlTitle>{item.title}</ControlTitle>
              </div>

              <ChevronIcon name="chevron" size="18" $opened={opened} />
            </ElemButton>

            {opened && renderMenu(item.children, level + 1)}
          </div>
        )
      }

      const active = isItemActive(item)

      return (
        <Elem
          key={key}
          title={item.title}
          href={item.href}
          isActive={active}
          aria-selected={active}
          onClick={item?.onClick ? item.onClick : (e) => navClick(e, item.href)}
          style={{ '--menu-item-icon': item.iconColor }}
        >
          <ControlIcon>
            {item?.iconHref ? (
              <IconImg src={item.iconHref} />
            ) : (
              <Icon name={item.icon} />
            )}
          </ControlIcon>
          <ControlTitle>{item.title}</ControlTitle>
        </Elem>
      )
    })

  const renderProjectMenu = useMemo(
    () => renderMenu(projectMenu),
    [projectMenu, router.asPath, openGroups]
  )

  const renderGeneralMenu = useMemo(
    () => renderMenu(generalMenu),
    [generalMenu, router.asPath, openGroups]
  )

  return (
    <>
      {editProjectOpened && (
        <ProjectEditModal
          headerTitle="Редактирование проекта"
          isOpened={true}
          onCloseHandler={() => setEditProjectOpened(false)}
          data={projectInfo}
        />
      )}

      <Wrapper>
        {visibleProjectMenu && (
          <ProjectMenu $hide={hide}>
            <ProjectMenuBox>
              {projectInfo?.slug ? (
                <Link href={`/projects/${projectInfo.slug}`}>
                  <a>{projectMenuTitle}</a>
                </Link>
              ) : (
                projectMenuTitle
              )}

              <ProjectMenuList>{renderProjectMenu}</ProjectMenuList>
            </ProjectMenuBox>
          </ProjectMenu>
        )}

        <List>{renderGeneralMenu}</List>

        <ConfidentialityNotice>
          КОНФИДЕНЦИАЛЬНО.
          <br />
          Информация, содержащаяся в данном сервисе является Конфиденциальной
          информацией ООО «ДИДЖИТАЛ СЕКТОР ПОДДЕРЖКА». Разглашение или
          распространение без согласия правообладателя запрещено.
          <br />
          Общество с ограниченной ответственностью «ДИДЖИТАЛ СЕКТОР ПОДДЕРЖКА»
          <br />
          ОГРН 1182375095187, ИНН 2312277308 <br />
          Адрес: 350058, Краснодарский край, г. Краснодар, ул. Старокубанская,
          д. 114, офис 314
        </ConfidentialityNotice>
      </Wrapper>
    </>
  )
}
